import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { ArrowRight, BookOpen, Cpu, Filter, Shield } from 'lucide-react';

const FourStagePipelineSlide = () => {
  const stages = [
    {
      num: 1,
      title: 'Cold Start SFT',
      icon: BookOpen,
      color: 'primary',
      description: 'Bootstrap readable reasoning',
      details: [
        'Thousands of curated long CoT examples',
        'Few-shot prompted outputs',
        'Cleaned R1-Zero outputs',
        'Human-annotated refinements'
      ],
      format: '<think>reasoning</think><summary>'
    },
    {
      num: 2,
      title: 'Reasoning RL',
      icon: Cpu,
      color: 'secondary',
      description: 'Large-scale GRPO training',
      details: [
        'Math, coding, science, logic tasks',
        'Language consistency rewards',
        'Prevents Chinese-English mixing',
        'Train until benchmark convergence'
      ],
      format: null
    },
    {
      num: 3,
      title: 'Rejection Sampling',
      icon: Filter,
      color: 'accent',
      description: 'Quality-filtered SFT',
      details: [
        '600K reasoning samples (filtered)',
        '200K non-reasoning samples',
        '800K total examples',
        '2 epochs of training'
      ],
      format: null
    },
    {
      num: 4,
      title: 'All-Scenario RL',
      icon: Shield,
      color: 'chart-4',
      description: 'Final multi-objective GRPO',
      details: [
        'Rule-based rewards (reasoning)',
        'Model-based rewards (helpfulness)',
        'Harmlessness evaluation',
        'Full response assessment'
      ],
      format: null
    }
  ];

  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="From R1-Zero's raw emergence to production-ready reasoning"
      >
        DeepSeek-R1: The 4-Stage Pipeline
      </SlideTitle>
      
      <SlideContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stages.map((stage, index) => {
            const Icon = stage.icon;
            return (
              <div key={index} className="relative">
                <div className={`slide-card h-full border-${stage.color}/30 hover:border-${stage.color}/60 transition-all`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-8 h-8 rounded-lg bg-${stage.color}/10 border border-${stage.color}/20 flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 text-${stage.color}`} />
                    </div>
                    <div className={`w-6 h-6 rounded-full bg-${stage.color}/20 flex items-center justify-center text-xs font-bold text-${stage.color}`}>
                      {stage.num}
                    </div>
                  </div>
                  
                  <h3 className="font-heading font-semibold text-foreground mb-1">{stage.title}</h3>
                  <p className={`text-xs text-${stage.color} mb-3`}>{stage.description}</p>
                  
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {stage.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className={`text-${stage.color}`}>•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {stage.format && (
                    <div className="mt-3 p-2 rounded bg-muted/50 font-mono text-xs text-muted-foreground">
                      {stage.format}
                    </div>
                  )}
                </div>
                
                {index < stages.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20">
            <p className="text-sm">
              <strong className="text-destructive">R1-Zero Issues Fixed:</strong>
              <span className="text-muted-foreground"> Poor readability, language mixing, weak non-reasoning performance</span>
            </p>
          </div>
          <div className="p-4 rounded-xl bg-accent/10 border border-accent/20">
            <p className="text-sm">
              <strong className="text-accent">R1 Result:</strong>
              <span className="text-muted-foreground"> Production-ready reasoning with human-friendly formatting</span>
            </p>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default FourStagePipelineSlide;
